/**
 * A tool's JSON schema read as the fields a person fills in: one per top-level property, with
 * its kind, whether the tool requires it, and its choices when it has any.
 */

type ToolFieldKind = 'text' | 'integer' | 'number' | 'boolean' | 'choice' | 'json';

export interface ToolField {
	name: string;
	kind: ToolFieldKind;
	required: boolean;
	description: string | null;
	options: string[];
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
	typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/** A nullable property's type is the one that is not null. */
const baseType = (property: Record<string, unknown>): unknown =>
	Array.isArray(property['type']) ? property['type'].find((entry) => entry !== 'null') : property['type'];

const hasChoices = (property: Record<string, unknown>): boolean =>
	Array.isArray(property['enum']) && property['enum'].length > 0;

const kindOf = (property: Record<string, unknown>): ToolFieldKind => {
	if (hasChoices(property)) return 'choice';
	const type = baseType(property);
	if (type === 'string') return 'text';
	if (type === 'integer') return 'integer';
	if (type === 'number') return 'number';
	if (type === 'boolean') return 'boolean';
	return 'json';
};

/** A tool's identity: names repeat across integrations, so the integration is part of it. */
export const toolKey = (provider: string | null, name: string): string => `${provider ?? ''}:${name}`;

/** The inputs a tool's schema marks required; nothing when it names them in any other shape. */
export const requiredOf = (schema: unknown): string[] => {
	const required = asRecord(schema)?.['required'];
	return Array.isArray(required) ? required.filter((name): name is string => typeof name === 'string') : [];
};

export const schemaFields = (schema: unknown): ToolField[] => {
	const properties = asRecord(asRecord(schema)?.['properties']);
	if (!properties) return [];
	const required = new Set(requiredOf(schema));
	return Object.entries(properties).map(([name, raw]) => {
		const property = asRecord(raw) ?? {};
		const description = typeof property['description'] === 'string' ? property['description'] : null;
		const options = Array.isArray(property['enum']) ? property['enum'].map((option) => String(option)) : [];
		return { name, kind: kindOf(property), required: required.has(name), description, options };
	});
};

const TYPE_IN_WORDS: Record<string, string> = {
	string: 'text',
	integer: 'whole number',
	number: 'number',
	boolean: 'yes or no',
	array: 'list',
	object: 'object'
};

/** Each top-level property's type in words, as a tool's details name it. */
export const typeLabels = (schema: unknown): Record<string, string> => {
	const properties = asRecord(asRecord(schema)?.['properties']) ?? {};
	return Object.fromEntries(
		Object.entries(properties).map(([name, raw]) => {
			const property = asRecord(raw) ?? {};
			const type = baseType(property);
			const label = hasChoices(property) ? 'choice' : typeof type === 'string' ? TYPE_IN_WORDS[type] : undefined;
			return [name, label ?? 'any'];
		})
	);
};

/** What an argument's value looks like when no schema says: text for text, JSON for anything else. */
export const kindOfValue = (value: unknown): ToolFieldKind => {
	if (typeof value === 'string') return 'text';
	if (typeof value === 'number') return 'number';
	if (typeof value === 'boolean') return 'boolean';
	return 'json';
};
